import { Injectable } from '@angular/core';
import { FiImageSize, FileType, ObjectTypes } from '@fi-shared/types';
import { saveAs } from 'file-saver';
import { FiBackendService, IBackendRequestOptions } from '@fi-shared/fi-backend/fi-backend.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '@fi-shared/app-text';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';

@Injectable()
export class FiPrintDownloadService {
    private currentDownloads: { [prop: string]: boolean } = {};

    constructor(
        private fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private formatDateService: FiFormatDateService,
        private fiSnackbarService: FiSnackBarService,
        private appTextService: AppTextService,
    ) {}

    private _isDownloadingInProcess$ = new BehaviorSubject<boolean>(false);
    get isDownloadingInProcess$(): Observable<boolean> {
        // this will be always false for now, then we can remove it
        return this._isDownloadingInProcess$.asObservable();
    }

    downloadBinaryFile(
        url: string,
        args: {
            fileName: string;
            size: FiImageSize;
            data?: any; // body
            getMethod?: boolean;
            absolutePath?: boolean;
            allowMultipleDownloads?: boolean;
        },
    ): Promise<void> {
        const fileName = args.fileName;
        const downloadingKey = this.getDownloadingKey(fileName, args.size);

        if (!!this.currentDownloads[downloadingKey] && !args.allowMultipleDownloads) {
            return Promise.reject<void>();
        }
        this.currentDownloads[downloadingKey] = true;

        const opts: IBackendRequestOptions = {
            reqConfig: {
                observe: 'response',
                responseType: 'blob',
            },
            absolutePath: args.absolutePath,
            skipContentTypeHeader: true,
        };
        const promise = args.getMethod
            ? this.fiBackend.fetchData<Blob>(url, opts)
            : this.fiBackend.fetchDataPost<Blob>(url, args.data, opts);

        //this._isDownloadingInProcess$.next(true);
        const loadingSnackBarRef = this.fiSnackbarService.showSnackbar({
            textMessage: `${this.appTextService.get('common.loading')} ${fileName}`,
            durationMs: Number.MAX_SAFE_INTEGER,
        });

        const resultPromise = promise
            .then((data) => {
                saveAs(data, args.fileName);
                this.fiSnackbarService.showSnackbar({
                    textMessage: `${this.appTextService.get('statistic.file_downloaded')} ${fileName}`,
                });
            })
            .finally(() => {
                //this._isDownloadingInProcess$.next(false);
                loadingSnackBarRef.dismiss();
                delete this.currentDownloads[downloadingKey];
            });
        return resultPromise;
        //return this.fiLoading.trackPromise(resultPromise);
    }

    private getDownloadingKey(fileName: string, size: FiImageSize): string {
        return `${fileName}-@@@-${size}`;
    }

    getReportFileName(prefix: string, reportFormatType: FileType): string {
        const extension = this.getFileExtension(reportFormatType);
        const fileName = prefix + '_' + this.formatDateService.getDateYYYYMMDD(new Date(), '') + extension;
        return fileName;
    }

    private getFileExtension(formatType: FileType): string {
        if (formatType === FileType.Excel) {
            return '.xlsx';
        }
        if (formatType === FileType.Pdf) {
            return '.pdf';
        }
    }

    generatePreviewFileImage(objectType: ObjectTypes, objectId: number, source: string) {
        return this.fiBackend.fetchDataPost<void>('MediaBank/FileUpload/GeneratePreviewFileImage', {
            ObjectType: objectType,
            ObjectId: objectId,
            Source: source,
            ImageSizes: [FiImageSize.Thumbnail, FiImageSize.Small],
        });
    }

    generatePdf(source: string): IFiHttpPromise<string> {
        return this.fiBackend.fetchDataPost<string>('MediaBank/FileUpload/GeneratePdf', {
            Source: source,
        });
    }

    getFormPrintElement(requestUrl: string): HTMLFormElement {
        const form: HTMLFormElement = document.createElement('form');
        form.setAttribute('action', requestUrl);
        form.setAttribute('target', 'printDialog');
        form.setAttribute('enctype', 'application/json');
        form.setAttribute('method', 'post');
        return form;
    }

    getInputElement(inputValue: string, nameAttrValue: string): HTMLInputElement {
        const inputElement: HTMLInputElement = document.createElement('input');
        inputElement.setAttribute('name', nameAttrValue);
        inputElement.setAttribute('type', 'hidden');
        inputElement.value = inputValue;
        return inputElement;
    }
}
